/**
 * Redirect URL Validator
 * Prevents open redirect attacks by validating redirect URLs against a whitelist
 */

const ALLOWED_DOMAINS = [
  'localhost',
  'kc.iskconkanpur.org',
  'admin.iskconkanpur.org',
  'www.iskconkanpur.org',
  'dmp.iskconkanpur.org',
  // Allow any subdomain of iskconkanpur.org
];

const DEFAULT_REDIRECT = 'https://kc.iskconkanpur.org';

/**
 * Validates if a redirect URL is safe to use
 * @param redirectUrl - The URL to validate
 * @returns The validated URL or default if invalid
 */
export function validateRedirectUrl(redirectUrl: string | null | undefined): string {
  if (!redirectUrl) {
    return DEFAULT_REDIRECT;
  }

  try {
    const url = new URL(redirectUrl);
    
    // Allow localhost for development
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      return redirectUrl;
    }

    // Check exact domain match
    if (ALLOWED_DOMAINS.includes(url.hostname)) {
      return redirectUrl;
    }

    // Check wildcard match for *.iskconkanpur.org
    if (url.hostname.endsWith('.iskconkanpur.org') || url.hostname === 'iskconkanpur.org') {
      return redirectUrl;
    }

    console.warn(`Blocked redirect to unauthorized domain: ${url.hostname}`);
    return DEFAULT_REDIRECT;
  } catch (error) {
    console.error('Invalid redirect URL format:', error);
    return DEFAULT_REDIRECT;
  }
}

/**
 * Gets a safe redirect URL from query parameters
 * @param searchParams - URLSearchParams object
 * @param paramName - Name of the redirect parameter (default: 'redirect')
 * @returns Validated redirect URL
 */
export function getSafeRedirectUrl(
  searchParams: URLSearchParams, 
  paramName: string = 'redirect'
): string {
  const redirectParam = searchParams.get(paramName);
  const decodedUrl = redirectParam ? decodeURIComponent(redirectParam) : null;
  return validateRedirectUrl(decodedUrl);
}
